import { isAllowedUrl } from '@/lib/crm/utils/urlValidation';

export interface UrlValueLabelProps {
  value: string | null | undefined;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Format a URL for display by stripping protocol and trailing slash.
 */
function formatDisplayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

/**
 * Ensure a URL has a protocol. Prepends https:// if missing.
 * Does not modify URLs that already have a protocol.
 */
function ensureProtocol(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('://')) {
    return url;
  }
  // Handle protocol-relative URLs (e.g., //example.com)
  if (url.startsWith('//')) {
    return `https:${url}`;
  }
  // Check if URL has a protocol with :// (e.g., file://, ftp://)
  // These will fail the isAllowedUrl check and be rendered as plain text
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
    return url;
  }
  // Handle host:port URLs (e.g., example.com:8080) - treat as needing https
  if (/^[^/:]+:\d+(?:\/|$)/.test(url)) {
    return `https://${url}`;
  }
  // Check if URL has some other protocol without :// (e.g., javascript:, data:)
  // These will fail the isAllowedUrl check and be rendered as plain text
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
    return url;
  }
  return `https://${url}`;
}

/**
 * Display component for URL values.
 * Renders valid http/https URLs as clickable links with security protections.
 * Invalid or dangerous URLs are rendered as plain text.
 *
 * Used in:
 * - Contact lists
 * - Contact detail views
 * - Any read-only URL display
 *
 * @example
 * <UrlValueLabel value="https://example.com/page" />
 * // Renders: <a href="https://example.com/page" target="_blank" rel="noopener noreferrer">example.com/page</a>
 *
 * @example
 * <UrlValueLabel value="example.com" />
 * // Renders: <a href="https://example.com" target="_blank" rel="noopener noreferrer">example.com</a>
 *
 * @example
 * <UrlValueLabel value="javascript:alert(1)" />
 * // Renders: <span>javascript:alert(1)</span> (plain text, not a link)
 */
export const UrlValueLabel: React.FC<UrlValueLabelProps> = ({
  value,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  if (!value) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  const fullUrl = ensureProtocol(trimmedValue);

  if (!isAllowedUrl(fullUrl)) {
    return (
      <span
        className={`${sizeClass} ${truncateClasses} break-all ${className}`}
        title={truncate ? trimmedValue : undefined}
      >
        {trimmedValue}
      </span>
    );
  }

  return (
    <a
      href={encodeURI(fullUrl)}
      target='_blank'
      rel='noopener noreferrer'
      onClick={e => e.stopPropagation()}
      className={`${sizeClass} ${truncateClasses} hover:underline break-all ${className}`}
      title={truncate ? trimmedValue : undefined}
      aria-label={trimmedValue}
    >
      {formatDisplayUrl(trimmedValue)}
    </a>
  );
};
